import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { List, Map as MapIcon, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import SearchBar from '../components/common/SearchBar';
import ListingCard from '../components/common/ListingCard';
import ListingMap from '../components/common/ListingMap';
import { listingsAPI, specialtiesAPI } from '../lib/api';

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [listings, setListings] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [showFilters, setShowFilters] = useState(false);
  const [totalResults, setTotalResults] = useState(0);

  // Filter states
  const [filters, setFilters] = useState({
    type: searchParams.get('type') || '',
    specialty: searchParams.get('specialty') || '',
    minRating: searchParams.get('rating') || '',
    insurance: searchParams.get('insurance') || '',
    sort: searchParams.get('sort') || 'relevance',
  });

  const query = searchParams.get('q') || '';
  const location = searchParams.get('location') || '';

  useEffect(() => {
    loadSpecialties();
  }, []);

  useEffect(() => {
    loadListings();
  }, [searchParams]);

  const loadSpecialties = async () => {
    try {
      const res = await specialtiesAPI.getAll();
      setSpecialties(res.data);
    } catch (error) {
      console.error('Error loading specialties:', error);
    }
  };

  const loadListings = async () => {
    setLoading(true);
    try {
      const params = {
        search: query,
        city: location,
        listing_type: filters.type || undefined,
        specialty: filters.specialty || undefined,
        min_rating: filters.minRating || undefined,
        insurance: filters.insurance || undefined,
        sort: filters.sort,
      };

      const res = await listingsAPI.getAll(params);
      setListings(res.data);
      setTotalResults(res.data.length);
    } catch (error) {
      console.error('Error loading listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = ({ query: q, location: loc }) => {
    const params = new URLSearchParams(searchParams);
    if (q) params.set('q', q);
    else params.delete('q');
    if (loc) params.set('location', loc);
    else params.delete('location');
    setSearchParams(params);
  };

  const updateFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      specialty: '',
      minRating: '',
      insurance: '',
      sort: 'relevance',
    });
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (location) params.set('location', location);
    setSearchParams(params);
  };

  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'relevance').length;

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Listing Type */}
      <div>
        <Label className="text-sm font-medium mb-2 block">Listing Type</Label>
        <Select value={filters.type || 'all'} onValueChange={(v) => updateFilter('type', v === 'all' ? '' : v)}>
          <SelectTrigger data-testid="filter-type">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="facility">Facilities</SelectItem>
            <SelectItem value="specialist">Specialists</SelectItem>
            <SelectItem value="pharmacy">Pharmacies</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Specialty */}
      <div>
        <Label className="text-sm font-medium mb-2 block">Specialty</Label>
        <Select value={filters.specialty || 'all'} onValueChange={(v) => updateFilter('specialty', v === 'all' ? '' : v)}>
          <SelectTrigger data-testid="filter-specialty">
            <SelectValue placeholder="All Specialties" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Specialties</SelectItem>
            {specialties.map((s) => (
              <SelectItem key={s.specialty_id} value={s.name}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Min Rating */}
      <div>
        <Label className="text-sm font-medium mb-2 block">Minimum Rating</Label>
        <Select value={filters.minRating || 'any'} onValueChange={(v) => updateFilter('minRating', v === 'any' ? '' : v)}>
          <SelectTrigger data-testid="filter-rating">
            <SelectValue placeholder="Any Rating" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any Rating</SelectItem>
            <SelectItem value="4">4+ Stars</SelectItem>
            <SelectItem value="3">3+ Stars</SelectItem>
            <SelectItem value="2">2+ Stars</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Insurance */}
      <div>
        <Label className="text-sm font-medium mb-2 block">Insurance Accepted</Label>
        <Input
          placeholder="e.g., Aetna, BlueCross"
          value={filters.insurance}
          onChange={(e) => updateFilter('insurance', e.target.value)}
          data-testid="filter-insurance"
        />
      </div>

      {/* Clear Filters */}
      {activeFilterCount > 0 && (
        <Button variant="outline" className="w-full" onClick={clearFilters}>
          Clear All Filters
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="search-page">
      {/* Sticky Search Header */}
      <div className="sticky top-16 z-40 bg-white border-b border-gray-200 py-4">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <SearchBar onSearch={handleSearch} />
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-8">
        {/* Results Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-gray-900">
              {query ? `Results for "${query}"` : 'All Listings'}
              {location && ` in ${location}`}
            </h1>
            <p className="text-gray-600 mt-1">{totalResults} results found</p>
          </div>

          <div className="flex items-center gap-4">
            {/* Sort */}
            <Select value={filters.sort} onValueChange={(v) => updateFilter('sort', v)}>
              <SelectTrigger className="w-[180px]" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="relevance">Relevance</SelectItem>
                <SelectItem value="rating">Highest Rated</SelectItem>
                <SelectItem value="reviews">Most Reviewed</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
              </SelectContent>
            </Select>

            {/* View Mode Toggle */}
            <div className="hidden md:flex border rounded-lg p-1 bg-gray-100">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-white shadow-sm' : ''}
                data-testid="view-list-btn"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'map' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('map')}
                className={viewMode === 'map' ? 'bg-white shadow-sm' : ''}
                data-testid="view-map-btn"
              >
                <MapIcon className="w-4 h-4" />
              </Button>
            </div>

            {/* Mobile Filter Button */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="md:hidden relative" data-testid="mobile-filter-btn">
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Filters
                  {activeFilterCount > 0 && (
                    <Badge className="ml-2 bg-teal-700">{activeFilterCount}</Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px]">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <FilterContent />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Active Filters */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {filters.type && (
              <Badge variant="secondary" className="pl-3">
                {filters.type}
                <button onClick={() => updateFilter('type', '')} className="ml-2 hover:text-red-500">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {filters.specialty && (
              <Badge variant="secondary" className="pl-3">
                {filters.specialty}
                <button onClick={() => updateFilter('specialty', '')} className="ml-2 hover:text-red-500">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {filters.minRating && (
              <Badge variant="secondary" className="pl-3">
                {filters.minRating}+ Stars
                <button onClick={() => updateFilter('minRating', '')} className="ml-2 hover:text-red-500">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {filters.insurance && (
              <Badge variant="secondary" className="pl-3">
                {filters.insurance}
                <button onClick={() => updateFilter('insurance', '')} className="ml-2 hover:text-red-500">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
          </div>
        )}

        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden md:block w-64 shrink-0">
            <div className="filter-sidebar sticky top-36">
              <h2 className="font-heading font-semibold text-lg mb-6 flex items-center">
                <SlidersHorizontal className="w-5 h-5 mr-2" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge className="ml-2 bg-teal-700">{activeFilterCount}</Badge>
                )}
              </h2>
              <FilterContent />
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl h-80 skeleton" />
                ))}
              </div>
            ) : viewMode === 'list' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {listings.length > 0 ? (
                  listings.map((listing) => (
                    <ListingCard key={listing.listing_id} listing={listing} />
                  ))
                ) : (
                  <div className="col-span-full text-center py-12">
                    <p className="text-gray-500 text-lg">No listings found matching your criteria</p>
                    <Button variant="outline" className="mt-4" onClick={clearFilters}>
                      Clear Filters
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                <ListingMap listings={listings} className="h-[500px]" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {listings.slice(0, 4).map((listing) => (
                    <ListingCard key={listing.listing_id} listing={listing} />
                  ))}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Search;
